import React, { useRef, useState } from 'react';
import { DragOutlined, FormOutlined, DeleteOutlined, PlusSquareOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { Button, Col, Row } from 'antd';
import { ButtonLoadMoreItems } from './ButtonLoadMoreItems';
import { ModalEditNode } from './ModalEditNode';
import { NodeSlot } from './NodeSlot';
import { useListNodes } from './hooks/useListNodes';
import { ChildNodesWrapper, ContentItem, ItemsCrud, NodeName } from './style';
import { useNotifications } from '../../../../hooks/useNotifications';
import { useDictionaryTranslation } from '../../../../hooks/useDictionaryTranslation';
import { debounceFunction } from '../../../../utility/utility';

const CustomListNode = ({ path, nodeData, renderChildNodes, nodeIndex }) => {
  const dndType = 'customListNode';
  const { showConfirmNotification } = useNotifications();
  const { translate: t } = useDictionaryTranslation();
  const listNodesData = useListNodes();
  const nodeRef = useRef();
  const nodeContainerRef = useRef();
  const [modalEditNodeData, setModalEditNodeData] = useState({
    show: false,
    nodeData: null,
    actionHandler: null,
  });
  const hasDescription = nodeData.description?.trim?.().length > 0

  const toggleChildNodes = debounceFunction(() => {
    const value = !nodeData.showChildNodes;
    listNodesData.updateNodeData({
      nodeId: nodeData.id,
      path,
      nodeData: {
        showChildNodes: value,
      },
    })
    if (value) {
      listNodesData.getListNodesPaginated({
        nodeId: nodeData.id,
        path,
        page: 1,
      });
    }
  }, 250);

  const closeNodesModal = () => {
    setModalEditNodeData({ show: false });
  };

  const onDeleteNodeList = () => {
    showConfirmNotification({
      title: t('areYouSure'),
      content: t('recordWillBeRemoved', { recordDescription: nodeData.title }),
      onOk: async () => {
        await listNodesData.deleteListNode({ path, nodeId: nodeData.id });
      },
    });
  };

  const onEditCurrentNode = () => {
    setModalEditNodeData({
      show: true,
      nodeData,
      actionHandler: async (nodeId, values) => {
        listNodesData.updateListNode({ path, nodeId, nodeData: values });
      },
    });
  };

  const handleAddNode = () => {
    setModalEditNodeData({
      show: true,
      nodeData: null,
      actionHandler: async (_, values) => {
        listNodesData.addListNode({
          path: [...path, nodeData.id],
          nodeData: { ...values, id: String(new Date().getTime()) },
        });
      },
    });
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (listNodesData.dnd.draggedNode?.id === nodeData.id) return;
    if (nodeContainerRef.current !== event.target && nodeRef.current !== event.target) return;
    const isBefore = event.nativeEvent.offsetY < event.target.clientHeight / 2;
    const targetIndex = isBefore ? nodeIndex : nodeIndex + 1;
    listNodesData.dnd.updateDropTarget({
      targetIndex,
      targetPath: path,
      anchorNode: nodeData.id,
    });
  };

  const handleDragStart = (event) => {
    event.dataTransfer.setData(
      'text/plain',
      JSON.stringify({
        type: dndType,
        data: {
          id: nodeData.id,
          path,
        },
      }),
    );
    listNodesData.dnd.startDragging({
      draggedNode: {
        id: nodeData.id,
        title: nodeData.title,
        description: nodeData.description,
        path,
        nodeIndex,
      },
    });
  };

  const handleDragEnd = (event) => {
    event.preventDefault();
    listNodesData.dnd.stopDragging();
  };

  return (
    <>
      <div
        id={nodeData.id}
        ref={nodeContainerRef}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
      >
        {listNodesData.dnd.anchorNode === nodeData.id && nodeIndex === 0 && listNodesData.dnd.targetIndex === 0 ? (
          <NodeSlot path={path} nodeData={listNodesData.dnd.draggedNode} />
        ) : null}

        <ContentItem ref={nodeRef} draggable onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <Row>
            <Col md={14}>
              {nodeData.hasChildren ? (
                <Button
                  onClick={toggleChildNodes}
                  icon={<DragOutlined style={{ fontSize: '12px' }} className="iconDrag" />}
                />
              ) : null}
              <NodeName>{nodeData.title}</NodeName>
            </Col>
            <Col md={hasDescription ? 24 : 7}>
              <p>{nodeData.description}</p>
            </Col>

            <Col md={hasDescription ? 24 : 3}>
              <Row justify="end">
                <ItemsCrud>
                  <Button type="secondary" onClick={handleAddNode} icon={<PlusSquareOutlined />} />
                  <Button type="secondary" onClick={onEditCurrentNode} icon={<FormOutlined />} />
                  <Button type="secondary" onClick={onDeleteNodeList} icon={<DeleteOutlined />} />
                </ItemsCrud>
              </Row>
            </Col>
          </Row>
        </ContentItem>
        {nodeData.showChildNodes ? (
          <>
            <ChildNodesWrapper>
              {renderChildNodes(nodeData.nodes)}
              <ButtonLoadMoreItems
                path={path}
                nodeId={nodeData.id}
                page={nodeData.page}
                pageSize={nodeData.pageSize}
                total={nodeData.total}
              />
            </ChildNodesWrapper>
          </>
        ) : null}
        {listNodesData.dnd.anchorNode === nodeData.id && listNodesData.dnd.targetIndex === nodeIndex + 1 ? (
          <NodeSlot path={path} nodeData={listNodesData.dnd.draggedNode} />
        ) : null}
      </div>

      {modalEditNodeData.show ? (
        <ModalEditNode
          show={modalEditNodeData.show}
          handleClose={closeNodesModal}
          nodeData={modalEditNodeData.nodeData}
          saveNode={modalEditNodeData.actionHandler}
        />
      ) : null}
    </>
  );
};

CustomListNode.propTypes = {
  nodeData: PropTypes.object,
  renderChildNodes: PropTypes.func,
  path: PropTypes.array,
  nodeIndex: PropTypes.number,
};

export default CustomListNode;
